import React from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { IconGoogle } from '../../../../app/icons/google';
import { IconApple } from '../../../../app/icons/apple';
import { IconMail } from '../../../../app/icons/mail';
import { IconLocked } from '../../../../app/icons/locked';
import { Divider } from '../../../../app/divider/divider';
import { Button } from '../../../../app/form/button/button';
import { useRootStore } from '../../../../../providers/root-store-provider';
import { ConsentRequiredAction } from '../consent-required-action';
import { WebButton } from '../../../button/web-button';

interface ThirdPartyLoginPropsInterface {
    compact?: boolean;
}

export const ThirdPartyLogin: React.FC<ThirdPartyLoginPropsInterface> = observer(({ compact = false }) => {
    const { t } = useTranslation();
    const router = useRouter();
    const {
        authStore: { authDialogStore },
        alertStore,
        cookieService,
    } = useRootStore();

    const onGoogleLogin = () => {
        if (authDialogStore.activeModal !== 'signUp' || authDialogStore.agreedTerms) {
            cookieService.setAgreedTerms(authDialogStore.agreedTerms);
            cookieService.setAgreedMarketing(authDialogStore.agreedMarketing);

            router.push(`${process.env.NEXT_PUBLIC_API_URL}/auth/google`);
        } else {
            alertStore.setErrorMessage(t('layout.forms.common.agreeTermsValidation'));
        }
    };

    const onAppleLogin = () => {
        if (authDialogStore.activeModal !== 'signUp' || authDialogStore.agreedTerms) {
            cookieService.setAgreedTerms(authDialogStore.agreedTerms);
            cookieService.setAgreedMarketing(authDialogStore.agreedMarketing);

            router.push(`${process.env.NEXT_PUBLIC_API_URL}/auth/apple`);
        } else {
            alertStore.setErrorMessage(t('layout.forms.common.agreeTermsValidation'));
        }
    };

    const isDisabled = authDialogStore.activeModal === 'signUp' && !authDialogStore.agreedTerms;
    const isSignUp = authDialogStore.activeModal === 'signUp';

    const onAuthMethodChange = () => {
        if (compact && authDialogStore.activeModal !== 'login' && authDialogStore.activeModal !== 'signUp') {
            authDialogStore.setLoginActive();
            authDialogStore.setAuthMethod('password');
            return;
        }

        authDialogStore.setAuthMethod(authDialogStore.authMethod === 'password' ? 'magicLink' : 'password');
    };

    const authMethodButtonLabel =
        authDialogStore.authMethod === 'password'
            ? t(isSignUp ? 'dialogs.magicLink.signUpWithLink' : 'dialogs.magicLink.loginWithLink')
            : t(isSignUp ? 'dialogs.magicLink.signUpWithPassword' : 'dialogs.magicLink.loginWithPassword');

    if (compact) {
        const googleButton = (
            <WebButton
                size="large"
                block
                type="button"
                variant="secondary"
                variation="outlined"
                tone="neutral"
                icon={<IconGoogle />}
                iconPosition="left"
                onClick={onGoogleLogin}
                disabled={isDisabled}
            >
                {t('dialogs.common.viaGoogle')}
            </WebButton>
        );
        const appleButton = (
            <WebButton
                size="large"
                block
                type="button"
                variant="secondary"
                variation="outlined"
                tone="neutral"
                icon={<IconApple />}
                iconPosition="left"
                onClick={onAppleLogin}
                disabled={isDisabled}
            >
                {t('dialogs.common.viaApple')}
            </WebButton>
        );

        return (
            <div className="grid gap-vsmall sm:grid-cols-2">
                <div className="sm:col-span-2" data-auth-navigation>
                    <WebButton
                        size="large"
                        block
                        type="button"
                        variant="secondary"
                        variation="outlined"
                        tone="neutral"
                        icon={authDialogStore.authMethod === 'password' ? <IconMail /> : <IconLocked />}
                        iconPosition="left"
                        onClick={onAuthMethodChange}
                    >
                        {authMethodButtonLabel}
                    </WebButton>
                </div>

                {isDisabled ? <ConsentRequiredAction>{googleButton}</ConsentRequiredAction> : googleButton}

                {isDisabled ? <ConsentRequiredAction>{appleButton}</ConsentRequiredAction> : appleButton}
            </div>
        );
    }

    return (
        <>
            <Divider gap="large">{t('dialogs.common.or')}</Divider>

            <div className="grid gap-vsmall">
                <Button
                    block
                    type="button"
                    variant="secondary"
                    variation="outlined"
                    icon={authDialogStore.authMethod === 'password' ? <IconMail /> : <IconLocked />}
                    onClick={onAuthMethodChange}
                >
                    {authMethodButtonLabel}
                </Button>

                {isDisabled ? (
                    <ConsentRequiredAction>
                        <Button
                            block
                            type="button"
                            onClick={onGoogleLogin}
                            variant="google"
                            icon={<IconGoogle />}
                            disabled
                        >
                            {t('dialogs.common.loginGoogle')}
                        </Button>
                    </ConsentRequiredAction>
                ) : (
                    <Button block type="button" onClick={onGoogleLogin} variant="google" icon={<IconGoogle />}>
                        {t('dialogs.common.loginGoogle')}
                    </Button>
                )}

                {isDisabled ? (
                    <ConsentRequiredAction>
                        <Button
                            block
                            type="button"
                            onClick={onAppleLogin}
                            variant="apple"
                            icon={<IconApple />}
                            disabled
                        >
                            {t('dialogs.common.loginApple')}
                        </Button>
                    </ConsentRequiredAction>
                ) : (
                    <Button block type="button" onClick={onAppleLogin} variant="apple" icon={<IconApple />}>
                        {t('dialogs.common.loginApple')}
                    </Button>
                )}
            </div>
        </>
    );
});
